import styled, { css } from "styled-components";

const RECOMMENDED_BG = "#F7FBFF";
const ALTERNATE_ROW_BG = "#F6F6F6";

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 500;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: stretch;
  justify-content: center;
`;

export const Panel = styled.div`
  width: 100%;
  max-width: 480px;
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

/* ── Header area (title + filters) ── */

export const HeaderWrapper = styled.div`
  background: #fff;
  box-shadow: 0 4px 2px rgba(0, 0, 0, 0.08);
  z-index: 2;
  flex-shrink: 0;
  padding-top: max(0px, env(safe-area-inset-top));
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
`;

export const BackButton = styled.button`
  border: none;
  background: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BackArrowSvg = styled.svg`
  width: 24px;
  height: 24px;
`;

export const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin-left: 12px;
  font-family: "Lato", sans-serif;
`;

/* ── Filter tabs ── */

export const FilterTabsRow = styled.div`
  display: flex;
  flex-direction: row;
  padding: 12px 16px;
  gap: 8px;
`;

export const FilterTab = styled.button<{ $active?: boolean }>`
  padding: 8px 8px;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$active ? "#008CFF" : "#D8D8D8")};
  background: ${(p) => (p.$active ? "#EAF5FF" : "#fff")};
  cursor: pointer;
  white-space: nowrap;
  font-family: "Lato", sans-serif;
`;

export const FilterTabText = styled.span<{ $active?: boolean }>`
  font-size: 12px;
  color: ${(p) => (p.$active ? "#008CFF" : "#4A4A4A")};
  font-weight: ${(p) => (p.$active ? 900 : 400)};
  font-family: "Lato", sans-serif;
`;

/* ── Table + footer wrapper ── */

export const TableAndFooterWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
`;

export const TableScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f2f2f2;
  -webkit-overflow-scrolling: touch;
`;

export const TableScrollContent = styled.div`
  padding-top: 16px;
  padding-bottom: 140px;
`;

/* ── Table header ── */

export const TableHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid #d8d8d8;
  position: relative;
  background: #fff;
`;

const headerCellBase = css`
  flex: 1;
  padding: 12px 4px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  min-height: 80px;
  position: relative;
  overflow: visible;
  border-right: 1px solid #d8d8d8;
`;

export const TableHeaderCellFirst = styled.div`
  ${headerCellBase}
  flex: 0.85;
  padding-left: 16px;
  background: #fff;
  align-items: flex-start;
`;

export const TableHeaderCell = styled.div<{
  $isLast?: boolean;
  $recommended?: boolean;
}>`
  ${headerCellBase}
  ${(p) => p.$isLast && "border-right: none;"}
  ${(p) => p.$recommended && `background: ${RECOMMENDED_BG};`}
`;

export const HeaderBenefitsLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  font-family: "Lato", sans-serif;
`;

export const HeaderPlanName = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #000;
  text-align: center;
  font-family: "Lato", sans-serif;
`;

/* ── Badge ── */

export const BadgeWrap = styled.div<{ $gradient?: string }>`
  position: absolute;
  top: -12px;
  padding: 4px 12px;
  border-radius: 16px;
  z-index: 1;
  background: ${(p) => p.$gradient || "#000"};
`;

export const BadgeText = styled.span<{ $color?: string }>`
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.$color || "#fff"};
  font-family: "Lato", sans-serif;
`;

/* ── Table body rows ── */

export const TableRow = styled.div<{ $alternate?: boolean }>`
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid #d8d8d8;
  min-height: 52px;
  background: ${(p) => (p.$alternate ? ALTERNATE_ROW_BG : "#fff")};
`;

const cellBase = css`
  flex: 1;
  padding: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-right: 1px solid #d8d8d8;
`;

export const TableCellFirst = styled.div<{ $alternate?: boolean }>`
  ${cellBase}
  flex: 0.9;
  align-items: flex-start;
  justify-content: center;
  padding-left: 16px;
  background: ${(p) => (p.$alternate ? ALTERNATE_ROW_BG : "#fff")};
`;

export const TableCell = styled.div<{
  $isLast?: boolean;
  $recommended?: boolean;
  $recommendedAlt?: boolean;
}>`
  ${cellBase}
  ${(p) => p.$isLast && "border-right: none;"}
  ${(p) => p.$recommended && `background: ${RECOMMENDED_BG};`}
  ${(p) => p.$recommendedAlt && `background: ${ALTERNATE_ROW_BG};`}
`;

/* ── Benefit label cell content ── */

export const BenefitCellContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

export const BenefitIconWrap = styled.div`
  width: 24px;
  height: 24px;
  margin-right: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

export const BenefitIconImg = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

export const BenefitTextRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
`;

export const BenefitLabel = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #000;
  flex: 1;
  font-family: "Lato", sans-serif;
`;

/* ── Value cells (icon or text) ── */

export const CellValueIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

export const CellValueText = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #000;
  text-align: center;
  font-family: "Lato", sans-serif;
`;

/* ── Tooltip ── */

export const TooltipTrigger = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-left: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const TooltipInfoIcon = styled.img`
  width: 16px;
  height: 16px;
  opacity: 0.6;
`;

export const TooltipOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 600;
`;

export const TooltipBubble = styled.div`
  position: absolute;
  background: #007e7d;
  border-radius: 8px;
  padding: 12px 40px 12px 20px;
  max-width: 60vw;
  min-width: 200px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  z-index: 601;
`;

export const TooltipArrow = styled.div`
  position: absolute;
  left: -8px;
  top: 16px;
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 8px solid #007e7d;
`;

export const TooltipText = styled.span`
  font-size: 14px;
  color: #fff;
  line-height: 20px;
  font-weight: 400;
  font-family: "Lato", sans-serif;
`;

export const TooltipCloseBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  font-family: "Lato", sans-serif;
`;

/* ── Footer (sticky bottom) ── */

export const FooterContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background: #fff;
  border-top: 1px solid #d8d8d8;
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
`;

export const FooterRow = styled.div`
  display: flex;
  flex-direction: row;
`;

export const FooterCellFirst = styled.div`
  flex: 0.9;
  padding: 12px 12px 2px 16px;
  display: flex;
  align-items: flex-end;
  border-right: 1px solid #d8d8d8;
`;

export const FooterPriceLabel = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #757575;
  font-family: "Lato", sans-serif;
`;

export const FooterCell = styled.div<{
  $isLast?: boolean;
  $recommended?: boolean;
}>`
  flex: 1;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-right: 1px solid #d8d8d8;
  ${(p) => p.$isLast && "border-right: none;"}
  ${(p) => p.$recommended && `background: ${RECOMMENDED_BG};`}
`;

export const FooterPriceWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  margin-bottom: 8px;
`;

export const FooterPrice = styled.span`
  font-size: 16px;
  font-weight: 900;
  color: #000;
  font-family: "Lato", sans-serif;
`;

export const FooterUnit = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #757575;
  margin-bottom: 1px;
  font-family: "Lato", sans-serif;
`;

export const BuyButton = styled.button`
  width: 100%;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: linear-gradient(90deg, #53b2fe 0%, #065af3 100%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BuyButtonText = styled.span`
  font-size: 12px;
  font-weight: 900;
  color: #fff;
  font-family: "Lato", sans-serif;
`;
